import React, { useEffect, useState } from "react";
import agent from "../../api/agent";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { UserSurveyResultDto } from "../../models/userSurveyResultDto";
import { Button, Dimmer, Loader, Segment, Message, Grid, GridRow, GridColumn } from "semantic-ui-react";
import { useStore } from "../../stores/store";
import { useTranslation } from "react-i18next";
import ProficiencyLevels_EN from "../ProficiencyLevelsControls/proficiencyLevels_en";
import ProficiencyLevels_RU from "../ProficiencyLevelsControls/proficiencyLevels_ru";
import ProficiencyLevels_UZ from "../ProficiencyLevelsControls/proficiencyLevels_uz";
import ProficiencyLevels_VN from "../ProficiencyLevelsControls/proficiencyLevels_vn";
import ProficiencyLevels_Id from "../ProficiencyLevelsControls/proficiencyLevels_id";
import MeterChart from "../GraphControls/meterChart";
import BarChart from "../GraphControls/barChart";
import PieChart from "../GraphControls/pieChart";
import MiniMeter from "../GraphControls/miniMeter";
import ProficiencyLevels_KH from "../ProficiencyLevelsControls/proficiencyLevels_kh";
import parse from "html-react-parser";
import ProficiencyLevels_KH_EN from "../ProficiencyLevelsControls/proficiencyLevels_kh_en";

interface Params {
  surveyCode: string;
  userSurveyCode: string;
}

export default function UserSurveyResults() {
  let { surveyCode, userSurveyCode } = useParams<Params>();
  const { commonStore } = useStore();
  const { t } = useTranslation("common");
  const { i18n } = useTranslation("common");
  const [results, setResults] = useState<UserSurveyResultDto>();
  const [generatingReport, setGeneratingReport] = useState<boolean>(false);
  //const [reportLanguages, setReportLanguages] = useState<LanguageDto[]>();

  const handleError = (error: any) =>
    toast.error(
      "An error has occurred while processing your request. Please try again or contact your system admin"
    );

  useEffect(() => {
    setGeneratingReport(true);
    commonStore.setSurveyCode(surveyCode);

    //commonStore.setSurveyCode("uzb");
    let language = commonStore.lang ? commonStore.lang : commonStore.defaultLang;
    agent.Translations.getSurveyContentByUserSurveyCode(userSurveyCode, language).then((result) => {
      commonStore.setSurveyConentTranslations(result);
    });

    agent.UserSurveys.load(userSurveyCode)
      .then((result) => {
        setResults(result);
        commonStore.setCurrentSurveyId(result.surveyId);
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        setGeneratingReport(false);
      });

    //load report languages
    /*agent.UserSurveys.languages(userSurveyCode)
      .then((result) => {
        setReportLanguages(result);
      })
      .catch((error) => {
        toast.error("An error has occurred while loading report languages");
      })
      .finally(() => {
        setLoadingLanguages(false);
      });*/
  }, [userSurveyCode]);

  useEffect(() => {
    let language = commonStore.lang ? commonStore.lang : commonStore.defaultLang;
    agent.Translations.getSurveyContentByUserSurveyCode(userSurveyCode, language).then((result) => {
      commonStore.setSurveyConentTranslations(result);
    });

    //reset results to force relaod
    setResults(results);
  }, [commonStore.lang]);

  useEffect(() => {
    commonStore.setSurveyCode(surveyCode);
    if (results == null || results == undefined) return;

    setTimeout(() => {
      downloadReport(commonStore.lang ? commonStore.lang : commonStore.defaultLang);
    }, 3000);
  }, [results]);

  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);

  const downloadReport = (language: string) => {
    setDownloadingFile(true);
    if (results == null || results == undefined) return;
    if (language === commonStore.lang) {
      downloadFile();
      return;
    }

    commonStore.setLanguage(language);
    agent.Translations.getSurveyContentByUserSurveyCode(userSurveyCode, language).then((result) => {
      commonStore.setSurveyConentTranslations(result);
      commonStore.setLanguage(language);
      i18n.changeLanguage(language);
      downloadFile();
    });
  };

  const downloadFile = () => {
    const staticElement: HTMLElement | null = document.getElementById(`userSurveyReport`);
    if (staticElement == null) return;
    agent.UserSurveys.downloadReport(userSurveyCode, staticElement.innerHTML);
    setDownloadingFile(false);
  };

  let currentSectionIndex = 0;
  let subSectionOrder = 1;
  let subQuestionOrder = 0;
  const getQuestionOrder = (currentSectionId: number): string => {
    if (currentSectionId != currentSectionIndex) {
      subSectionOrder = subSectionOrder + 1;
      currentSectionIndex = currentSectionIndex + 1;
      subQuestionOrder = 1;
    } else {
      subQuestionOrder = subQuestionOrder + 1;
    }

    let order: string = subSectionOrder.toString() + "." + subQuestionOrder.toString();
    return order;
  };

  const loader = (
    <Segment className="injaz-loader-tbl">
      <Dimmer active inverted>
        <Loader size="large">{t("UserSurveyResult.GeneratingReport")} ...</Loader>
      </Dimmer>
    </Segment>
  );

  const report = (
    <div
      id="userSurveyReport"
      style={{
        boxShadow: "0px 0px 20px 11px rgba(246,246,246,1)",
        padding: "46px",
        background: "#fff",
        marginTop: "20px",
      }}
    >
      <div
        style={
          commonStore.lang === "kh"
            ? {
                fontFamily: "Siemreap",
              }
            : {
                fontFamily: "inherit",
              }
        }
      >
        <div>
          <h1>{results && commonStore.getSurveyResultTitle(results)}</h1>{" "}
        </div>
        <div style={{ marginTop: "25px", marginBottom: "10px", fontWeight: "bold", fontSize: "1.2em" }}>
          {t("UserSurveyResult.OverallScore")}:{" "}
          <span style={{ color: "#2185d0" }}>{results?.scorePercentage} %</span>
        </div>
        <div style={{ marginTop: "10px", marginBottom: "25px", fontWeight: "bold", fontSize: "1.2em" }}>
          {t("UserSurveyResult.OverallLevel")}:{" "}
          <span style={{ color: "#2185d0" }}>
            {results?.overallLevel && t(`Common.${results?.overallLevel}`)}
          </span>
        </div>
        <hr />
        <div>
          <h3 style={{ textDecoration: "underline", marginTop: "30px", marginBottom: "30px" }}>
            {t("UserSurveyResult.YourSelfReflectionOverallResults")}
          </h3>
          <Grid style={{ placeContent: "center" }}>
            <GridRow>
              {results && (
                <MeterChart
                  score={results.scorePercentage}
                  level={results.overallLevel}
                  surveyCode={surveyCode}
                  meterPoints={results ? results.surveyPoints : 6}
                />
              )}
            </GridRow>
          </Grid>

          <Grid stackable style={{ width: "auto", marginLeft: "0", marginRight: "0!important" }}>
            <Grid.Row
              centered
              columns={4}
              style={{ display: "block", textAlign: "center", marginBottom: "25px" }}
            >
              <Grid.Column
                style={{ position: "relative", display: "inline-block", width: "25% !important" }}
              >
                <div>
                  <MiniMeter
                    score={results?.preSurveyQuestionScore}
                    meterPoints={results ? results.surveyPoints : 6}
                  />{" "}
                  <span style={{ marginLeft: "50px" }}> {t("Common.PreSurveyQuestion")} </span>
                </div>
              </Grid.Column>
              <Grid.Column
                style={{ position: "relative", display: "inline-block", width: "25% !important" }}
              >
                <div>
                  <MiniMeter
                    score={results?.postSurveyQuestionScore}
                    meterPoints={results ? results.surveyPoints : 6}
                  />{" "}
                  <span style={{ marginLeft: "50px" }}>{t("Common.PostSurveyQuestion")}</span>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <hr />
          <p style={{ pageBreakAfter: "always" }}></p>
          <h3 style={{ textDecoration: "underline", marginTop: "30px", marginBottom: "30px" }}>
            {t("UserSurveyResult.YourSelfReflectionResultsByArea")}
          </h3>
          <div style={{ width: "90%", margin: "0 auto" }}>
            {results && <BarChart sections={results.sections} />}
          </div>
        </div>
        <hr />
        <h3 style={{ textDecoration: "underline", marginTop: "30px", marginBottom: "30px" }}>
          {t("UserSurveyResult.YourSelfReflectionResultsByArea")}
        </h3>
        <Grid>
          <GridRow>
            <GridColumn width={1}></GridColumn>
            <GridColumn width={14}>
              {results && (
                <PieChart
                  sections={results?.sections}
                  meterPoints={results ? results.surveyPoints : 6}
                  questionSet={results.questionSet}
                />
              )}
            </GridColumn>
            <GridColumn width={1}></GridColumn>
          </GridRow>
        </Grid>

        <hr />
        <p style={{ pageBreakAfter: "always" }}></p>
        {results?.sections.map((section, sectionIndex) => (
          <div
            id="hide-sub-quest"
            className="hide-sub-quest"
            style={{ paddingTop: "25px" }}
            key={section.sectionId}
          >
            <h2 style={{ color: "#2185d0" }}>
              {commonStore.getUserSurveySectionTitle(section)} - {section.scorePercentage}%
            </h2>
            <div
              style={{ marginTop: "25px", marginBottom: "10px", fontWeight: "bold", fontSize: "1.2em" }}
            >
              {t("Common.Level")}:{" "}
              <span style={{ fontWeight: "bolder", paddingBottom: "10px" }}>
                {" "}
                {section.level && t(`Common.${section.level}`)}
              </span>
            </div>
            <div style={{ marginTop: "20px" }}>
              {section.questionResults &&
                section.questionResults.length > 0 &&
                section.questionResults.map((question) => (
                  <div key={question.questionId}>
                    <div style={{ color: "#2185d0", marginTop: "20px", display: "flex" }}>
                      {getQuestionOrder(sectionIndex)}
                      {": "}
                      {parse(commonStore.getuserSurveyQuestionBody(question))}
                    </div>
                    <br />
                    <div style={{ marginTop: "20px" }}>
                      <span style={{ fontWeight: "bolder" }}>{t("Common.YourResponse")}</span>
                      {"  "} {parse(commonStore.getUserSurveyQuestionChoiceBody(question))}
                    </div>
                    <div style={{ marginTop: "20px" }}>
                      <span style={{ fontWeight: "bolder" }}>{t("Common.Score")}</span>
                      <span style={{ color: "#2185d0" }}>
                        {" "}
                        {question.userScore}/{question.totalScore}
                      </span>
                    </div>
                    <div style={{ marginTop: "20px" }}>
                      <span style={{ fontWeight: "bolder" }}></span>
                      {question.tip == null
                        ? "-"
                        : parse(commonStore.getUserSurveyQuestionChoiceTip(question))}
                    </div>
                    <div style={{ marginTop: "20px" }}>
                      {question.levelUp != null && question.levelUp != "" && (
                        <span style={{ fontWeight: "bolder" }}>{t("Common.LevelUp")}</span>
                      )}{" "}
                      {parse(commonStore.getUserSurveyQuestionChoiceLevelUp(question))}
                    </div>
                    <br />
                    <hr />
                  </div>
                ))}
            </div>
          </div>
        ))}

        <p style={{ pageBreakAfter: "always" }}></p>
        <div style={{ marginTop: "30px", marginBottom: "20px" }}>
          {results &&
            commonStore.lang &&
            commonStore.lang === "en" &&
            results.questionSet.toLowerCase() === "uzb" && <ProficiencyLevels_EN />}
          {results &&
            commonStore.lang &&
            commonStore.lang === "en" &&
            results.questionSet.toLowerCase() === "kh" && <ProficiencyLevels_KH_EN />}
          {results && commonStore.lang && commonStore.lang === "uz" && <ProficiencyLevels_UZ />}
          {results && commonStore.lang && commonStore.lang === "ru" && <ProficiencyLevels_RU />}
          {results && commonStore.lang && commonStore.lang === "kh" && <ProficiencyLevels_KH />}
          {results && commonStore.lang && commonStore.lang === "vn" && <ProficiencyLevels_VN />}
          {results && commonStore.lang && commonStore.lang === "id" && <ProficiencyLevels_Id />}
        </div>
      </div>
    </div>
  );
  return (
    <>
      <h2 style={{ textAlign: "center", marginTop: "20px", marginBottom: "20px" }}>
        {t("UserSurveyResult.ThankYouMessage")}
      </h2>
      <Message color="yellow">
        <b>{t("UserSurveyResult.Important")}</b> {t("UserSurveyResult.NoDownloadAfterClose")}
        <br />
        {t("UserSurveyResult.CheckDeviceMessage")} <br />
        {t("UserSurveyResult.SelectDownloadButton")}
      </Message>
      {results && (
        <>
          <Button
            className="icon"
            icon="download"
            as="div"
            labelPosition="left"
            content={t("Common.DownloadReport")!}
            loading={generatingReport || downloadingFile}
            onClick={() => downloadReport(commonStore.lang ? commonStore.lang : "en")}
          ></Button>
          {/*<Dropdown button floating labeled icon="download" text={t("Common.DownloadReport")!}>
            <Dropdown.Menu className="lang-dd">
              {reportLanguages &&
                reportLanguages.length > 0 &&
                reportLanguages.map((language) => (
                  <Dropdown.Item key={language.id} disabled={loadingLanguages}></Dropdown.Item>
                ))}
            </Dropdown.Menu>
                </Dropdown>*/}

          <Button
            as="div"
            labelPosition="left"
            icon="download"
            content={t("Certificate.DownloadCertificate")}
            onClick={() => {
              window.open(
                `/certificate/${surveyCode}/${userSurveyCode}/preview?language=${commonStore.lang}`,
                "_blank",
                "noopener,noreferrer"
              );
            }}
          ></Button>
        </>
      )}
      {generatingReport && <h3>{t("UserSurveyResult.KeepBrowserOpenMessage")}</h3>}
      {results && !generatingReport && report}
      {generatingReport && loader}
    </>
  );
}
